/**
 * PageTable — renders a Table element on public pages.
 *
 * 0.36.157 - Stage 1:
 *  - Configurable headers + rows
 *  - Optional client-side sort (header click cycles neutral / asc / desc)
 *  - Adjustable width (defaults to 1168px)
 *  - Styling per spec:
 *    - Header height min 56px, expandable
 *    - Header bg #f5f5f5, cells bg white
 *    - Outer stroke --color-text-600 (#747474)
 *    - Inner lines --color-text-400 (#bfbfbf)
 *    - Header text black + bold, cells text not bold
 *    - Row min-height matches dropdown (40px)
 *  - Keyboard-selectable interactive cells (if their content is a link
 *    or button — Stage 2 feature; in Stage 1 cells are plain text).
 */
import { useState, useMemo, useEffect, useRef, useContext } from 'react';
import { usePaginationStateForTable, PaginationContext } from './PaginationContext';
import { SortNeutralIcon, SortAscIcon, SortDescIcon } from './SortIcons';
import styles from './PageTable.module.css';

/* 0.46.11 - Text weight and colour helpers. Only known weights and
   colour shapes are used; anything else is ignored rather than written
   into a style. */
function textWeight(w) {
  return w === 'regular' ? 400 : w === 'semibold' ? 600 : w === 'bold' ? 700 : null;
}
function textColor(c) {
  const v = String(c || '').trim();
  if (/^--[a-z0-9-]+$/i.test(v)) return `var(${v})`;
  if (/^#[0-9a-f]{3,8}$/i.test(v)) return v;
  return null;
}
function columnTextStyle(styles, ci) {
  const s = Array.isArray(styles) ? styles[ci] : null;
  if (!s || typeof s !== 'object') return {};
  const out = {};
  const w = textWeight(s.weight);
  const c = textColor(s.color);
  if (w) out.fontWeight = w;
  if (c) out.color = c;
  // 0.48.67 - The column's background.
  const bg = textColor(s.bg);
  if (bg) out.background = bg;
  return out;
}

/* 0.48.67 - Row colors: odd rows (1, 3, 5 as shown) and even rows,
   background and text, as custom properties on the row. */
function rowColorStyle(colors, ri) {
  if (!colors) return undefined;
  const odd = ri % 2 === 0;
  const bg = textColor(odd ? colors.row_bg_odd : colors.row_bg_even);
  const fg = textColor(odd ? colors.row_color_odd : colors.row_color_even);
  if (!bg && !fg) return undefined;
  return { ...(bg ? { '--pt-row-bg': bg } : {}), ...(fg ? { '--pt-row-color': fg } : {}) };
}

/* 0.46.22 - Column sizes: a smallest and a largest width per column,
 * one pair for large screens (768px and wider) and one for small ones.
 * Given to each header and body cell as CSS variables; the
 * .sizedCell rule (PageTable.module.css) picks the pair for the screen,
 * a side left empty falling back to the other screen's value.
 *
 *   sizes[i] = { min_l, max_l, min_s, max_s }   numbers, px
 *
 * The largest width is also the column's preferred width, so a column
 * sits at its largest size when there is room and gives way to its
 * smallest when there is not. With `lastRest` the last column has no
 * largest width: the table fills its space and the last column takes
 * what the others leave. */
const _px = (v) => (Number.isFinite(Number(v)) && Number(v) > 0 ? `${Number(v)}px` : null);
/* 0.46.30 - Rows whose REQUIRED cells are all filled. A column is
 * required by position (required[i] true); a cell is empty when its
 * text, trimmed, is "". Used by the renderers before the rows reach
 * the table, so the paginator counts what is shown.
 * 0.46.38 - required[i] may also be 'empty': that column must be EMPTY,
 * so a row is left out when its cell there is filled (current owners:
 * rows with no end date). */
const _cellEmpty = (v) => v === null || v === undefined
  || (typeof v === 'string' && v.trim() === '');

export function filterRequiredRows(rows, required, on) {
  if (!on || !Array.isArray(rows) || !Array.isArray(required) || !required.some(Boolean)) return rows;
  return rows.filter(row => !Array.isArray(row) || required.every((req, i) => {
    if (!req) return true;
    if (req === 'empty') return _cellEmpty(row[i]);
    return !_cellEmpty(row[i]);
  }));
}

/* 0.49.09 - The outer border in two parts: the top and bottom lines,
   and the side lines. hide_edge_lines (before 0.49.09) hid both and
   still does until either is changed. */
export function edgeLinesOf(el) {
  const both = !!(el && el.hide_edge_lines);
  const pick = (k) => (el && el[k] !== undefined && el[k] !== null ? !!el[k] : both);
  return { topBottom: pick('hide_top_bottom_lines'), sides: pick('hide_side_lines') };
}

/* 0.49.05 - "The last column gets the rest" for large and small screens
   separately: lastColRest is true (both), false, or { l, s }. */
export function restScreens(lastRest) {
  if (lastRest && typeof lastRest === 'object') return { l: !!lastRest.l, s: !!lastRest.s };
  return { l: !!lastRest, s: !!lastRest };
}
/* The element's setting: last_col_rest_l / last_col_rest_s, each falling
   back to last_col_rest (before 0.49.05 one setting for both screens). */
export function lastColRestOf(el) {
  const both = !!(el && el.last_col_rest);
  const pick = (k) => (el && el[k] !== undefined && el[k] !== null ? !!el[k] : both);
  return { l: pick('last_col_rest_l'), s: pick('last_col_rest_s') };
}

export function hasColumnSizes(sizes, lastRest) {
  const r = restScreens(lastRest);
  return r.l || r.s || (Array.isArray(sizes)
    && sizes.some(s => s && ['min_l', 'max_l', 'min_s', 'max_s'].some(k => _px(s[k]))));
}
function columnSizeStyle(sizes, ci, isLast, lastRest) {
  const s = (Array.isArray(sizes) && sizes[ci]) || {};
  const out = {};
  const set = (name, v) => { const px = _px(v); if (px) out[name] = px; };
  set('--pt-min-l', s.min_l);
  set('--pt-min-s', s.min_s);
  // The rest column's largest width does not apply on its screen
  // (the restCell classes say so there); the other screen keeps it.
  set('--pt-max-l', s.max_l);
  set('--pt-max-s', s.max_s);
  void isLast; void lastRest;
  return out;
}

export default function PageTable({
  name, headers = [], rows = [], sortable = true, width = 1168,
  /* 0.42.32 - Two Henaket table variants, added to the CANONICAL table
   * rather than hand-rolled beside it.
   *
   * showHeader: false omits the header row. `headers` is still passed -
   * it is what fixes the column count and binds the column widths - it
   * is simply not drawn.
   *
   * horizontalBordersOnly: no vertical lines at all, neither between
   * cells nor at the table's sides. Only the horizontal rules remain,
   * above, between and below the rows.
   *
   * First use: the list of chosen files under a File upload. */
  showHeader = true,
  horizontalBordersOnly = false,
  /* 0.46.09 - Each kind of line on its own: the outer border, the
   * vertical lines between columns, the horizontal lines between rows
   * (the header's bottom line among them). */
  hideEdgeLines = false,
  // 0.49.09 - Or each part of the outer border on its own.
  hideTopBottomLines = false,
  hideSideLines = false,
  hideVerticalLines = false,
  hideHorizontalLines = false,
  /* 0.46.11 - Text weight and colour: the header row as a whole, and
   * each data column. Weight 'regular' | 'semibold' | 'bold'; colour a
   * Henaket token (--color-...) or a hex. Empty = the table's default
   * (bold header, regular cells, text-800). */
  headerWeight = '',
  headerColor = '',
  columnTextStyles = null,
  // 0.48.67 - { header_bg, row_bg_odd, row_bg_even, row_color_odd,
  // row_color_even }: Henaket tokens (--color-...) or hex codes.
  tableColors = null,
  /* 0.46.22 - Smallest and largest widths per column, for large and
   * small screens, and "the last column gets the rest". When either is
   * set they replace colWidths (see columnSizeStyle). */
  colSizes = null,
  /* 0.48.99 - Alignment: colAlign[i] = { h, v } for the data columns,
     checkAlign and editAlign for the checkbox and Edit link columns.
     h is left, center or right (header and cells), v is top, middle
     or bottom (cells). Empty keeps the default. */
  colAlign = null,
  checkAlign = null,
  editAlign = null,
  lastColRest = false,
  /* 0.46.24 - A table without a header row can be drawn as
   * ONE column (0.46.25: any number of columns, was two), on large screens (768px and wider) and/or on small
   * ones: each row's first cell, then its second, then the next row.
   * Cells keep their own column's text style, so a bold first column
   * gives bold, regular, bold, regular. CSS only (.stackLarge,
   * .stackSmall), so the switch follows the screen without a render. */
  stackLarge = false,
  stackSmall = false,
  // 0.46.26 - Draw the resize handles visibly (Column widths preview).
  resizeHandlesVisible = false,
  // 0.46.30 - Tell the paginator how many rows are shown, when the
  // caller has taken some out (Hide a row when a required cell is
  // empty) and the table's own row count would be too high.
  reportShownRowCount = false,
  colWidths = null, fixedLayout = false, rowKeys = null,
  sortValues = null, wrapHeaderColumns = null,
  // 0.37.01 — When true, the table's minimum width equals the sum
  // of each column's max-content (widest single line without
  // breaking mid-word). Cells still wrap at whitespace; long
  // unbreakable tokens push their column wider rather than
  // clipping. If total width exceeds the container, the wrapper's
  // overflow-x: auto surfaces a horizontal scrollbar. Opt-in
  // because tables using explicit colWidths already sum to a
  // known width and don't want max-content growth interfering.
  //
  // WARNING: Do NOT combine with `fixedLayout={true}`. In a fixed-
  // layout table with explicit colgroup widths, `min-width:
  // max-content` still gets computed from raw content (widest
  // token in the biggest cell), then the browser stretches every
  // column proportionally to reach that width — columns balloon
  // past admin's dragged widths. Use `wrapAllCells` instead for
  // fixed-layout tables.
  growToContent = false,
  // 0.37.03 — When true, body cells break long unbroken tokens at
  // any character (word-break: break-word, overflow-wrap: anywhere).
  // Content stays inside the cell rectangle regardless of column
  // width; long tokens split across lines rather than escaping.
  // The complementary fix to growToContent for tables that MUST
  // keep fixedLayout + explicit colWidths (typically because they
  // support column resize via colgroup).
  wrapAllCells = false,
  // 0.37.09 - Integer count of leading columns treated as "action"
  // columns - icon-only buttons in narrow fixed-width cells. The
  // first N body cells get a tighter `.actionCell` padding (4px
  // vs the default 12px each side) so a 40px column can visually
  // hold a 32px icon button flush.
  // 0.38.11 - Header cells for the same N columns ALSO get
  // `.actionCell` now, which in addition to tighter padding
  // applies rigid min-width/max-width: 40px + box-sizing:
  // border-box. This locks action columns to 40px at cell level,
  // not just via the `<col>` element - preventing a browser
  // layout edge case where resizing a neighboring data column
  // could visibly change action-column widths under table-layout:
  // fixed. The 40px value is baked into the CSS; if a future
  // caller needs a different action-column width, promote it to
  // a prop.
  actionColumnCount = 0,
  // 0.40.24 - Like actionColumnCount, but for slots at the END of the
  // row. Those cells get .actionCell's tight sizing PLUS
  // .actionCellOutside, which strips the border and background so the
  // controls read as hanging beside the table rather than as another
  // column. Kept as a separate prop so existing callers using leading
  // action columns are untouched.
  trailingActionColumnCount = 0,
  // 0.48.64 - The last column holds a row's Edit link: as narrow as
  // its content (a pencil or a short link), never wrapped.
  fitLastColumn = false,
  // 0.37.11 — Confined-scroll pattern for wide + tall tables.
  //
  //   stickyHeader: <thead> row sticks to the top of the scroll
  //     container so column labels stay visible while scrolling
  //     down through many rows.
  //
  //   stickyLeadingColumns: N leftmost columns stick to the left
  //     edge of the scroll container so row-identifier cells
  //     (action icons + PK) stay visible while scrolling right
  //     through many data columns. Widths for the sticky columns
  //     are read from `colWidths` to compute the cumulative
  //     `left` offset per column. All sticky column widths must
  //     be specified in colWidths as pixel strings ('40px') or
  //     numbers — no null/auto columns in the sticky region.
  //
  //   maxHeight: CSS value for the scroll wrapper's max-height
  //     (e.g. 'calc(100vh - 300px)'). Confines vertical scroll to
  //     the wrapper so both scrollbars are always in the viewport
  //     without page-scrolling. Only takes effect when set.
  //
  // The three properties compose: stickyHeader alone (long thin
  // table), stickyLeadingColumns alone (short wide table), or
  // all three together (the canonical data-grid layout).
  stickyHeader = false,
  stickyLeadingColumns = 0,
  maxHeight = null,
  // 0.36.549 — Optional array of extra CSS class names to apply to
  // each <tr>. Same shape/order as `rows`. Used by
  // AdminLibraryDetail to grey out inactive entries. Any falsy entry
  // means "no extra class".
  rowClassNames = null,
  // 0.36.588 — Optional per-column resize. Two shapes:
  //   resizableColumns: bool[] — one per header; true = handle
  //   onColumnResize: (index: number, newWidthPx: number) => void
  // Handle: a 6 px grab strip on the header's right edge with
  // cursor: col-resize. Native mouse events; parent owns width
  // state. Non-resizable columns render no handle.
  resizableColumns = null,
  onColumnResize = null,
  // 0.36.537 — Controlled sort mode. When BOTH provided, the parent
  // owns sort state and receives header clicks via onSortChange
  // instead of PageTable running its own client-side sort. Enables
  // server-side sort: parent updates its state (typically URL
  // params) and refetches, PageTable just displays what it gets.
  controlledSort = null,      // { columnIndex: number, direction: 'asc'|'desc'|null } | null
  onSortChange = null,        // (nextState) => void
  // 0.36.667 — When true, prepends an extra first column of small
  // Henaket checkboxes (24×24, canonical spec — 2px near-black
  // border, sharp corners). Header cell is unlabelled. Check state
  // is tracked internally as a Set keyed by row index (or rowKey
  // when provided). Wiring to a form value is a follow-up ship.
  checkable = false,
  // 0.48.98 - Rows arrive ticked; the ticked rows' keys go to
  // onCheckedRows (in row order) whenever they or the rows change.
  checkAllFirst = false,
  onCheckedRows = null,
  // 0.36.671 — When true, PageTable skips its own row slicing.
  // Used by library-backed tables in server-paginated mode: the
  // caller has already fetched only the current page from the
  // server, so slicing again with paginationState.currentPage
  // would leave 0 rows. Row-index math for keys still starts at
  // (currentPage-1)*perPage so rowKey semantics stay consistent
  // with load-all mode.
  preSliced = false,
  // 0.36.677 — Optional per-column sortability override, aligned
  // with `headers`. When passed, `isSortableColumn(i)` becomes
  // `sortable && header non-empty && sortableColumns[i] !== false`.
  // Undefined entries default to sortable — same behavior as when
  // this prop isn't passed at all. Master `sortable` still gates
  // everything (turn it off, no column sorts).
  sortableColumns = null,
  // 0.36.682 — Selection mode. 'page' = header checkbox flips
  // visible rows only; 'all' = header engages an "allActive"
  // sentinel plus explicit-off tracking. Defaults to 'page'
  // when unset or unrecognised.
  selectionMode = 'page',
  // 0.36.684 — Stable key describing the current query context
  // (filters + search + any other row-count-affecting mechanism
  // in the caller). When it changes, PageTable resets `allActive`
  // + `explicitlyUnchecked` so that "select all" in `all` mode
  // means "all rows MATCHING CURRENT FILTERS", never leaks across
  // context changes. `explicitlyChecked` (page-mode picks)
  // survives — those name specific rowKeys and the user's intent
  // is unambiguous. Undefined/null → no query context (manual
  // tables and any caller without filters), reset never fires.
  // 0.36.716 — Two separate context keys.
  //   filterContextKey — changes when filters change; triggers
  //     selection reconciliation (rows dropping out of set lose
  //     selection).
  //   searchContextKey — changes when search text changes; does
  //     NOT touch selection (search is a temporary visual filter).
  //   queryContextKey — legacy alias; kept for callers that haven't
  //     migrated. When present without the split pair, treated as
  //     filterContextKey so behavior matches prior semantics.
  filterContextKey = null,
  searchContextKey = null,
  queryContextKey = null,
  // 0.36.685 — When true, every header cell wraps its text
  // (white-space: normal, word-break: break-word). Enables
  // narrower column drags on the editor canvas where admin would
  // otherwise be blocked by the header's intrinsic single-line
  // min-width. Runtime + Preview leave this false so headers
  // stay single-line as designed.
  wrapAllHeaders = false,
  // 0.36.692 — Callback fired whenever selection state changes.
  // Signature: (state) => void, where state has shape:
  //   { effectiveMode, allActive, explicitlyChecked: Set,
  //     explicitlyUnchecked: Set }
  // The parent (LibraryBackedTable) maps this to the concrete
  // set of picked full-row objects and publishes them to form
  // values. Omitted → no publishing; selection stays internal
  // (matches pre-0.36.692 behavior).
  onSelectionChange = null,
  // 0.36.700 — Command channel. When present, PageTable listens
  // for external selection commands. Shape:
  //   { command: 'select_all' | 'clear_all'
  //             | 'select_page' | 'clear_page',
  //     ts: <number> }
  // `ts` is compared against the last-seen timestamp to detect a
  // new command — same command with the same ts is idempotent (no
  // re-fire on re-renders). Buttons in the form editor drive this
  // via the `write_value` action.
  externalCommand = null,
}) {
  // 0.36.216 — variant prop ('default' / 'plain') REMOVED in 0.36.316.
  // 0.36.217 — colWidths optional prop for fixed column widths.
  // 0.36.407 — rowKeys optional prop, data-row-key per <tr>.
  // 0.36.409 — sortValues optional prop. When passed, sort uses
  // sortValues[i][j] as the sort key for row i, column j instead of
  // rows[i][j]. Needed when `rows` contains React elements (which
  // stringify to "[object Object]" and sort together). Callers pass
  // the raw underlying values here. Aligned with rows: same length,
  // and each inner array has the same length as headers.
  //
  // Per-header sortability: empty header strings are skipped from
  // sort UI (no button, no indicator). Callers use this to mark
  // action columns as non-sortable — those columns have '' labels.
  //
  // 0.36.414 — wrapHeaderColumns optional prop. Boolean array
  // aligned with headers. When true, that column's header text
  // is allowed to wrap onto multiple lines (default nowrap per
  // .headerText). Used for narrow columns whose header can be
  // broken into two words (e.g. "Numeric" / "code").
  const [internalSortState, setInternalSortState] = useState({ columnIndex: -1, direction: null });
  // 0.36.667 — Set of row identifiers currently checked (keyed by
  // rowKey when provided, else by originalIdx).
  //
  // 0.36.682 — Extended into a mode-aware selection model:
  //   - selectionMode: 'page' (default) or 'all' (from linked
  //     source element via `selectionMode` prop)
  //   - allActive: in 'all' mode, has the user activated the
  //     "select every row" flag?
  //   - explicitlyChecked: rows the user hand-picked ON (relevant
  //     in 'page' mode, or in 'all' mode after allActive was
  //     toggled off with some rows still individually kept on)
  //   - explicitlyUnchecked: rows the user hand-picked OFF while
  //     allActive is true
  //
  // Row-checked check:
  //   - 'page' mode: explicitlyChecked.has(key)
  //   - 'all' + allActive: !explicitlyUnchecked.has(key)
  //   - 'all' + !allActive: explicitlyChecked.has(key)
  //
  // For large libraries in 'all' mode, we don't enumerate all row
  // keys — allActive is a sentinel. This keeps memory / network
  // costs at O(explicit-toggles) instead of O(all-rows).
  const [explicitlyChecked, setExplicitlyChecked] = useState(() => new Set());
  const [explicitlyUnchecked, setExplicitlyUnchecked] = useState(() => new Set());
  const [allActive, setAllActive] = useState(false);
  // 0.36.702 — Radio-driven mode source removed. `selectionMode`
  // prop kept for signature/backward compat but is now ignored;
  // effectiveMode comes from commandedMode (via the button command
  // channel) or defaults to 'page'. Radio no longer has any effect
  // on the table.
  const [commandedMode, setCommandedMode] = useState(null);
  const effectiveMode = commandedMode !== null ? commandedMode : 'page';

  // 0.36.682 → 0.36.701 — Historically, flipping the mode-source
  // radio between 'page' and 'all' reset both explicit sets and
  // allActive on the reasoning that carrying picks across a mode
  // change was semantically ambiguous. In practice this made every
  // radio touch feel destructive: any accidental interaction wiped
  // ongoing work. With the explicit command-channel model
  // (ship 0.36.700) — buttons express select_all / clear_all
  // unambiguously — the defensive reset is redundant. Removed.
  //
  // Consequence: flipping 'all' → 'page' while allActive was true
  // leaves the sentinel dormant. Rows visually show as unchecked
  // in 'page' mode (which reads from explicitlyChecked). Flipping
  // back to 'all' resurfaces them. Nothing lost.

  // 0.36.684 → 0.36.716 — Selection reconciliation on FILTER
  // change. Search changes are intentionally NOT reconciled: search
  // is a visual spotlight over the same working set; clearing X
  // brings everything back including selections. Filter changes
  // redefine the working set — rows that fall out actively lose
  // their selection.
  //
  // Small-library case (rowKeys covers every filter-matching row):
  //   - If allActive was engaged, materialize into explicitlyChecked
  //     (current rowKeys minus explicitlyUnchecked). Clear
  //     allActive + explicitlyUnchecked + commandedMode. Result:
  //     the concrete rows that WERE selected become the new set.
  //   - Then intersect explicitlyChecked with the new rowKeys —
  //     drop selections that no longer match the filter.
  //   - Result matches the user's mental model: narrower filter
  //     drops out-of-set selections; wider filter leaves existing
  //     selections intact and does not auto-select newcomers.
  //
  // Large-library case (preSliced=true, rowKeys is only the visible
  // page): we can't check whether individual selections are still
  // in the filtered set without a "resolve keys against filter"
  // backend endpoint. For now we leave state as-is; stale
  // selections are inert (rows not matching won't appear checked).
  // Future ship can add a resolve endpoint for large-lib
  // reconciliation.
  //
  // Timing: filterContextKey updates immediately on parent state
  // change. rowKeys updates only after the LibraryBackedTable fetch
  // settles. So we mark a pending flag on filterContextKey change
  // and reconcile on the NEXT rowKeys update.
  // Prefer the explicit filterContextKey; fall back to the legacy
  // queryContextKey for callers that haven't migrated. Search
  // changes intentionally not reconciled — see block above.
  const effectiveFilterContextKey = filterContextKey != null
    ? filterContextKey
    : queryContextKey;
  const pendingFilterReconcileRef = useRef(false);
  const prevFilterContextRef = useRef(effectiveFilterContextKey);
  useEffect(() => {
    if (prevFilterContextRef.current === effectiveFilterContextKey) return;
    prevFilterContextRef.current = effectiveFilterContextKey;
    pendingFilterReconcileRef.current = true;
  }, [effectiveFilterContextKey]);
  useEffect(() => {
    if (!pendingFilterReconcileRef.current) return;
    pendingFilterReconcileRef.current = false;
    if (preSliced) return;  // large lib: punt
    const newKeys = new Set(rowKeys || []);
    if (allActive) {
      // Materialize into explicitlyChecked, then clear sentinel.
      const materialized = new Set();
      for (const k of newKeys) {
        if (!explicitlyUnchecked.has(k)) materialized.add(k);
      }
      setExplicitlyChecked(materialized);
      setExplicitlyUnchecked(new Set());
      setAllActive(false);
      setCommandedMode(null);
      return;
    }
    // Intersect explicitlyChecked with the new filtered set.
    let changed = false;
    const next = new Set();
    for (const k of explicitlyChecked) {
      if (newKeys.has(k)) next.add(k);
      else changed = true;
    }
    if (changed) setExplicitlyChecked(next);
  }, [rowKeys, preSliced, allActive, explicitlyChecked, explicitlyUnchecked]);

  const isRowChecked = (rowId) => {
    if (effectiveMode === 'all' && allActive) {
      return !explicitlyUnchecked.has(rowId);
    }
    return explicitlyChecked.has(rowId);
  };

  // 0.36.692 — Notify parent (LibraryBackedTable) of every
  // selection change so it can publish selected rows to form
  // values. Fires on mount too (with the initial empty state),
  // which is fine — LibraryBackedTable's publisher just calls
  // ctx.updateValue with an empty array, effectively clearing
  // the field. Downstream filters treat that as empty-source
  // and apply their empty_source_behavior.
  //
  // Callback held in a ref so identity churn on the parent side
  // (a new closure per render) doesn't cause the effect to fire
  // every render, which would loop.
  const onSelectionChangeRef = useRef(onSelectionChange);
  useEffect(() => { onSelectionChangeRef.current = onSelectionChange; },
    [onSelectionChange]);
  useEffect(() => {
    const fn = onSelectionChangeRef.current;
    if (!fn) return;
    fn({
      effectiveMode,
      allActive,
      explicitlyChecked,
      explicitlyUnchecked,
    });
  }, [effectiveMode, allActive, explicitlyChecked, explicitlyUnchecked]);

  /* 0.48.98 - Tick every row at first: a row key seen for the first
     time is ticked, also when a new answer brings more rows. A row the
     visitor unticked stays unticked. */
  const seenKeysRef = useRef(new Set());
  const rowKeysSig = (rowKeys || []).join('\u0001');
  useEffect(() => {
    if (!checkable || !checkAllFirst || preSliced || !Array.isArray(rowKeys)) return;
    const fresh = rowKeys.map(String).filter(k => !seenKeysRef.current.has(k));
    if (!fresh.length) return;
    fresh.forEach(k => seenKeysRef.current.add(k));
    setExplicitlyChecked(prev => {
      const next = new Set(prev);
      fresh.forEach(k => next.add(k));
      return next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowKeysSig, checkable, checkAllFirst, preSliced]);
  const onCheckedRowsRef = useRef(onCheckedRows);
  useEffect(() => { onCheckedRowsRef.current = onCheckedRows; }, [onCheckedRows]);
  useEffect(() => {
    const fn = onCheckedRowsRef.current;
    if (!fn || !checkable || !Array.isArray(rowKeys)) return;
    fn(rowKeys.map(String).filter(k => isRowChecked(k)));
  });

  const toggleRowChecked = (rowId) => {
    if (effectiveMode === 'all' && allActive) {
      // Row is currently checked (via allActive); user unchecks it
      // → add to explicitlyUnchecked. Or vice-versa: currently in
      // explicitlyUnchecked → user re-checks it → remove from there.
      setExplicitlyUnchecked(prev => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    } else {
      setExplicitlyChecked(prev => {
        const next = new Set(prev);
        if (next.has(rowId)) next.delete(rowId);
        else next.add(rowId);
        return next;
      });
    }
  };
  // 0.36.537 — Prefer parent's controlled sort state when provided,
  // fall back to the internal state otherwise. Keeps existing
  // callsites (which don't pass controlledSort) unchanged.
  const isControlled = controlledSort != null;
  const sortState = isControlled ? controlledSort : internalSortState;

  const isSortableColumn = (i) =>
    sortable
    && headers[i] !== ''
    && (sortableColumns === null || sortableColumns[i] !== false);

  // 0.36.427 — Split a "Label [XX]" header into (label, tag) so the
  // trailing language tag can be rendered with a distinct style
  // (blue). If the pattern doesn't match, returns (h, null) and the
  // caller renders the plain string. Detects [XX] or [XXX] (2–3
  // uppercase letters) at the end preceded by whitespace.
  const parseHeaderTag = (h) => {
    if (typeof h !== 'string') return { label: h, tag: null };
    const m = /^(.*?)\s+(\[[A-Z]{2,3}\])$/.exec(h);
    return m ? { label: m[1], tag: m[2] } : { label: h, tag: null };
  };
  const renderHeaderContent = (h, headerTextClass) => {
    const { label, tag } = parseHeaderTag(h);
    if (!tag) return <span className={headerTextClass}>{h}</span>;
    return (
      <span className={headerTextClass}>
        {label}{' '}
        <span className={styles.headerTag}>{tag}</span>
      </span>
    );
  };

  const handleHeaderClick = (columnIndex) => {
    if (!isSortableColumn(columnIndex)) return;
    // Compute the next sort state via the standard cycle:
    //   different column → asc
    //   same column asc → desc
    //   same column desc → null (unsorted)
    //   same column null → asc
    let next;
    if (sortState.columnIndex !== columnIndex) {
      next = { columnIndex, direction: 'asc' };
    } else {
      const dir = sortState.direction === 'asc' ? 'desc'
                : sortState.direction === 'desc' ? null
                : 'asc';
      next = { columnIndex: dir === null ? -1 : columnIndex, direction: dir };
    }
    if (isControlled) {
      onSortChange && onSortChange(next);
    } else {
      setInternalSortState(next);
    }
  };

  // 0.36.173 - if this table is linked to a Pagination element, the
  // displayedRows below is further sliced by currentPage * perPage.
  const paginationState = usePaginationStateForTable(name);
  const _pctx = useContext(PaginationContext);
  useEffect(() => {
    if (!reportShownRowCount || !_pctx || !_pctx.reportRowCount || !name) return;
    _pctx.reportRowCount(name, Array.isArray(rows) ? rows.length : 0);
  }, [reportShownRowCount, _pctx && _pctx.reportRowCount, name, rows && rows.length]);

  // 0.36.409 — Sort rows + rowKeys together to keep them aligned.
  // Also use sortValues if provided (raw values instead of stringified
  // React elements).
  // 0.36.537 — In controlled mode the server already returned rows
  // in the requested order — running the client-side sort on top
  // would double-sort and mis-order the page. Skip it.
  const { sortedRows, sortedRowKeys } = useMemo(() => {
    if (isControlled) {
      return { sortedRows: rows, sortedRowKeys: rowKeys };
    }
    if (sortState.columnIndex < 0 || !sortState.direction) {
      return { sortedRows: rows, sortedRowKeys: rowKeys };
    }
    const idx = sortState.columnIndex;
    const indices = rows.map((_, i) => i);
    const getSortValue = (rowIdx) => {
      if (sortValues && sortValues[rowIdx] && sortValues[rowIdx][idx] != null) {
        return sortValues[rowIdx][idx];
      }
      return rows[rowIdx]?.[idx];
    };
    indices.sort((ia, ib) => {
      const av = (getSortValue(ia) ?? '').toString();
      const bv = (getSortValue(ib) ?? '').toString();
      const aN = parseFloat(av);
      const bN = parseFloat(bv);
      if (!Number.isNaN(aN) && !Number.isNaN(bN) && av.trim() !== '' && bv.trim() !== '') {
        return sortState.direction === 'asc' ? aN - bN : bN - aN;
      }
      const cmp = av.localeCompare(bv);
      return sortState.direction === 'asc' ? cmp : -cmp;
    });
    const nextRows = indices.map(i => rows[i]);
    const nextKeys = rowKeys ? indices.map(i => rowKeys[i]) : null;
    return { sortedRows: nextRows, sortedRowKeys: nextKeys };
  }, [rows, rowKeys, sortValues, sortState, isControlled]);

  // 0.36.173 - slice for pagination if linked. Otherwise show all rows.
  // 0.36.671 — When `preSliced` is true, skip slicing: the caller has
  // already fetched exactly the rows for the current page (used by
  // library-backed tables in server-paginated mode).
  const displayedRows = useMemo(() => {
    if (preSliced) return sortedRows;
    if (!paginationState) return sortedRows;
    const start = (paginationState.currentPage - 1) * paginationState.perPage;
    return sortedRows.slice(start, start + paginationState.perPage);
  }, [sortedRows, paginationState, preSliced]);

  // 0.36.682 — Row keys aligned with `displayedRows` (visible page).
  // For sorted+sliced rows, we mirror the row-key math used in the
  // body render loop (using sortedRowKeys when provided, falling
  // back to a stable page-relative index). Used to compute the
  // header checkbox's tri-state and to drive its click action in
  // 'page' mode.
  // 0.36.705 → 0.36.706 — Effective row identities for the current
  // page. Bulletproofs against duplicate keys collapsing to shared
  // identity. Two separate offsets:
  //
  //   * `sortedKeysIdx` — used to READ from sortedRowKeys. That
  //     array is page-scoped in preSliced (server-paginated) mode
  //     and library-global otherwise, so we skip the offset for
  //     preSliced.
  //   * `globalIdx` — used for positional fallback keys. Always
  //     includes the offset (regardless of preSliced) so row 4 on
  //     page 1 and row 4 on page 112 don't collide to the same
  //     positional key. Without this, `select all` on page 1,
  //     unselect row 5, navigate to page 2 → page 2's row 5
  //     inherits the unselect.
  //
  // Fallback triggers on: sortedRowKeys unset/missing (empty
  // string) OR duplicates detected across visible rows. Duplicates
  // check considers only non-empty keys — empties are already
  // treated as missing.
  const displayedRowIds = useMemo(() => {
    const globalStart = paginationState
      ? (paginationState.currentPage - 1) * paginationState.perPage
      : 0;
    const keysStart = paginationState && !preSliced
      ? (paginationState.currentPage - 1) * paginationState.perPage
      : 0;
    const raw = displayedRows.map((_row, ri) => {
      const idx = keysStart + ri;
      return sortedRowKeys && sortedRowKeys[idx] != null
        ? String(sortedRowKeys[idx])
        : '';
    });
    const seen = new Set();
    let hasDup = false;
    for (const k of raw) {
      if (!k) continue;
      if (seen.has(k)) { hasDup = true; break; }
      seen.add(k);
    }
    return raw.map((k, ri) => {
      if (!k || hasDup) return String(globalStart + ri);
      return k;
    });
  }, [displayedRows, paginationState, preSliced, sortedRowKeys]);

  // Kept as alias for the parts of the code (header state, click
  // handler, selection publisher via onSelectionChange) that
  // already read `displayedRowKeys`. Same content now — the
  // memo above is authoritative.
  const displayedRowKeys = displayedRowIds;

  // 0.36.700 — External-command dispatcher. Watches
  // `externalCommand.ts`; on new timestamps, applies the command
  // to internal selection state.
  //
  // Commands:
  //   select_all — engage the "everything matching filter+search"
  //     sentinel. Forces effectiveMode='all', allActive=true, and
  //     clears both explicit sets.
  //   clear_all — clear everything: allActive=false, sets cleared,
  //     commandedMode reset to null so the linked mode-source
  //     radio (if any) regains control.
  //   select_page — mark all currently-visible rows as checked.
  //     Preserves the current mode; in 'all' mode + allActive, this
  //     means removing them from explicitlyUnchecked (they're
  //     already checked via the sentinel). Otherwise, adds them to
  //     explicitlyChecked.
  //   clear_page — inverse of select_page for the visible rows.
  const lastCommandTsRef = useRef(null);
  useEffect(() => {
    if (!externalCommand) return;
    const { command, ts } = externalCommand;
    if (ts == null || ts === lastCommandTsRef.current) return;
    lastCommandTsRef.current = ts;
    if (command === 'select_all') {
      setCommandedMode('all');
      setAllActive(true);
      setExplicitlyChecked(new Set());
      setExplicitlyUnchecked(new Set());
    } else if (command === 'clear_all') {
      setCommandedMode(null);
      setAllActive(false);
      setExplicitlyChecked(new Set());
      setExplicitlyUnchecked(new Set());
    } else if (command === 'select_page') {
      if (effectiveMode === 'all' && allActive) {
        setExplicitlyUnchecked(prev => {
          const next = new Set(prev);
          for (const k of displayedRowKeys) next.delete(k);
          return next;
        });
      } else {
        setExplicitlyChecked(prev => {
          const next = new Set(prev);
          for (const k of displayedRowKeys) next.add(k);
          return next;
        });
      }
    } else if (command === 'clear_page') {
      if (effectiveMode === 'all' && allActive) {
        setExplicitlyUnchecked(prev => {
          const next = new Set(prev);
          for (const k of displayedRowKeys) next.add(k);
          return next;
        });
      } else {
        setExplicitlyChecked(prev => {
          const next = new Set(prev);
          for (const k of displayedRowKeys) next.delete(k);
          return next;
        });
      }
    }
  }, [externalCommand, displayedRowKeys, effectiveMode, allActive]);

  // 0.36.682 — Tri-state header checkbox. Semantics:
  //   'all' mode + allActive + no explicit unchecks → CHECKED
  //   'all' mode + allActive + some unchecks         → INDETERMINATE
  //   'all' mode + !allActive + no explicit checks   → UNCHECKED
  //   'all' mode + !allActive + some explicit checks → INDETERMINATE
  //   'page' mode: purely derived from the visible page's
  //                explicitly-checked count. 0 → UNCHECKED,
  //                all → CHECKED, some → INDETERMINATE.
  // 0.36.704 — Header display always reflects the CURRENT PAGE's
  // visible rows only. Tri-state based on how many of the visible
  // rows are currently checked (using isRowChecked, which knows
  // about the sentinel). Doesn't look at global state — the header
  // no longer represents the whole table, just the page it's
  // sitting above.
  //
  //   0 visible checked → UNCHECKED
  //   all visible checked → CHECKED
  //   mixed → INDETERMINATE
  const headerCheckboxState = useMemo(() => {
    if (displayedRowKeys.length === 0) return 'unchecked';
    let cnt = 0;
    for (const k of displayedRowKeys) {
      if (effectiveMode === 'all' && allActive) {
        if (!explicitlyUnchecked.has(k)) cnt += 1;
      } else {
        if (explicitlyChecked.has(k)) cnt += 1;
      }
    }
    if (cnt === 0) return 'unchecked';
    if (cnt === displayedRowKeys.length) return 'checked';
    return 'indeterminate';
  }, [effectiveMode, allActive, explicitlyChecked, explicitlyUnchecked, displayedRowKeys]);

  // 0.36.704 — Header click always operates on the current page,
  // regardless of mode. Never touches allActive or commandedMode —
  // those are exclusively the button command channel's concern
  // (select_all / clear_all / etc.). Header display still respects
  // mode (tri-state based on visible rows), but its click just
  // adds/removes displayed keys to/from the appropriate set:
  //   - page mode: explicitlyChecked
  //   - all + allActive: explicitlyUnchecked (inverted semantics)
  const onHeaderCheckboxClick = () => {
    const checkVisible = headerCheckboxState !== 'checked';
    if (effectiveMode === 'all' && allActive) {
      // Sentinel mode: checking visible rows = removing them from
      // unchecks; unchecking = adding them to unchecks.
      setExplicitlyUnchecked(prev => {
        const next = new Set(prev);
        for (const k of displayedRowKeys) {
          if (checkVisible) next.delete(k);
          else next.add(k);
        }
        return next;
      });
    } else {
      // Page mode (or all mode without sentinel): toggle
      // displayedRowKeys in explicitlyChecked.
      setExplicitlyChecked(prev => {
        const next = new Set(prev);
        for (const k of displayedRowKeys) {
          if (checkVisible) next.add(k);
          else next.delete(k);
        }
        return next;
      });
    }
  };

  const containerStyle = { maxWidth: width };
  // 0.37.11 — Confined-scroll wrapper style. When maxHeight is set,
  // vertical scroll is confined to the wrapper (rather than the
  // page) and overflow-y flips from visible to auto so a vertical
  // scrollbar appears inside the wrapper when body height exceeds
  // maxHeight. overflow-x stays auto (from the base .tableScrollWrapper
  // rule).
  const wrapperStyleBase = maxHeight ? { ...containerStyle, maxHeight, overflowY: 'auto' } : containerStyle;
  // 0.46.11 - Header weight and colour reach the header cells (and the
  // sort button inside them) as custom properties.
  const wrapperStyle = {
    ...wrapperStyleBase,
    ...(textWeight(headerWeight) ? { '--pt-head-weight': textWeight(headerWeight) } : {}),
    ...(textColor(headerColor) ? { '--pt-head-color': textColor(headerColor) } : {}),
    ...(tableColors && textColor(tableColors.header_bg) ? { '--pt-head-bg': textColor(tableColors.header_bg) } : {}),
  };

  // 0.37.11 — Cumulative left offsets for sticky columns, in px.
  // Reads from colWidths. Each sticky column's `left` = sum of
  // the widths of preceding sticky columns.
  //
  //   colWidths = ['40px', '40px', '195px', '260px', ...]
  //   stickyLeadingColumns = 3
  //   → stickyLeftOffsets = [0, 40, 80]
  //
  // Non-pixel widths (null, 'auto', '%') in the sticky region
  // fall back to 0 — callers should always supply pixel widths
  // for sticky columns.
  const parsePx = (v) => {
    if (typeof v === 'number') return v;
    if (typeof v !== 'string') return 0;
    const m = v.match(/^\s*(\d+(?:\.\d+)?)\s*px\s*$/);
    return m ? parseFloat(m[1]) : 0;
  };
  const stickyLeftOffsets = [];
  if (stickyLeadingColumns > 0 && Array.isArray(colWidths)) {
    let acc = 0;
    for (let i = 0; i < stickyLeadingColumns; i++) {
      stickyLeftOffsets.push(acc);
      acc += parsePx(colWidths[i]);
    }
  }

  // Sticky-cell inline style for column index i (header or body).
  // Merges with any existing style the caller has set on the cell.
  // Header cells always get top: 0 (when stickyHeader). Sticky
  // leading columns also get left: <offset>.
  const stickyHeaderStyle = (i) => {
    const isStickyCol = i < stickyLeadingColumns;
    if (!stickyHeader && !isStickyCol) return null;
    const s = { position: 'sticky' };
    if (stickyHeader) s.top = 0;
    if (isStickyCol) {
      s.left = stickyLeftOffsets[i];
      // Corner cells (sticky header AND sticky col) sit above
      // both types of scrolled content.
      s.zIndex = stickyHeader ? 3 : 2;
    } else if (stickyHeader) {
      s.zIndex = 2;
    }
    // 0.37.11 → 0.37.12 → 0.37.23 — Every border on a sticky cell
    // must live in a box-shadow, not `border-*`. border-collapse:
    // collapse + position: sticky doesn't preserve collapsed borders
    // during scroll — they disappear at every scroll position.
    // box-shadow lives outside the border-collapse system and paints
    // reliably on positioned cells.
    //
    // Composition for sticky headers, always at least one of:
    //   inset -1px 0 0    right border   (only when isStickyCol)
    //   inset 0 -1px 0    bottom border  (always — the header
    //                     row's underline that keeps it visually
    //                     separated from body during scroll; user
    //                     reported this disappearing in 0.37.22)
    //   2px 0 4px         outer drop shadow — marks the frozen /
    //                     scrolling seam (only on the LAST sticky
    //                     column)
    // border-right on the base .headerCell CSS is set to none for
    // sticky cells to avoid clashing with the shadow.
    const stroke = 'var(--color-text-400)';
    const bottomBorder = `inset 0 -1px 0 ${stroke}`;
    // 0.40.29 - The vertical separator is drawn on a cell's LEFT edge,
    // not the previous cell's right edge. `inset -1px 0 0` paints
    // inside the cell's right edge, which is 1px to the LEFT of where
    // a collapsed border sits, so the header lines were offset from
    // the body's by a pixel. `inset 1px 0 0` on the following cell
    // lands on the boundary itself and lines up.
    const leftBorder = `inset 1px 0 0 ${stroke}`;
    const seamShadow = '2px 0 4px rgba(0, 0, 0, 0.08)';
    // 0.40.28 - Trailing action cells read as hanging outside the
    // table, so they take no vertical separators.
    // 0.40.29 - ...but they DO keep the bottom stroke, so the header's
    // underline runs unbroken across the whole row instead of stopping
    // short above the icons.
    const firstActionIdx = headers.length - trailingActionColumnCount;
    const isTrailingAction = trailingActionColumnCount > 0
      && i >= firstActionIdx;
    const shadows = [bottomBorder];
    // Every cell except the first gets a left separator. The FIRST
    // action cell still gets one: that line is the table's own right
    // edge, matching the body's last data cell.
    if (i > 0 && (!isTrailingAction || i === firstActionIdx)
        && !horizontalBordersOnly) {
      shadows.push(leftBorder);
    }
    if (!isTrailingAction && isStickyCol && i === stickyLeadingColumns - 1) {
      shadows.push(seamShadow);
    }
    s.borderRight = 'none';
    s.borderBottom = 'none';
    s.boxShadow = shadows.join(', ');
    return s;
  };
  const stickyBodyStyle = (i) => {
    if (i >= stickyLeadingColumns) return null;
    // See stickyHeaderStyle for the box-shadow rationale.
    const innerBorder = 'inset -1px 0 0 var(--color-text-400)';
    const seamShadow = '2px 0 4px rgba(0, 0, 0, 0.08)';
    return {
      position: 'sticky',
      left: stickyLeftOffsets[i],
      zIndex: 1,
      // Opaque background so scrolled cells behind don't bleed
      // through. Matches .table's white background.
      background: 'var(--pt-row-bg, #fff)',
      borderRight: 'none',
      boxShadow: i === stickyLeadingColumns - 1
        ? `${seamShadow}, ${innerBorder}`
        : innerBorder,
    };
  };

  // 0.36.691 — Drag preview state. When a column is being resized,
  // this holds the pending width (applied to the <col> element via
  // colgroup so the drag is visible in real time) and the current
  // cursor X (for the vertical guide line). null when no drag is
  // active. See the resize handle's onMouseDown for the drive loop.
  const [dragState, setDragState] = useState(null);

  // Effective width for column i, factoring in the active drag
  // preview. Used to render the <col> style so a drag is visible
  // even under table-layout: fixed (where inline th widths are
  // ignored — see 0.36.689).
  const colStyleFor = (i) => {
    if (dragState && dragState.columnIndex === i) {
      return { width: dragState.previewWidth };
    }
    const w = Array.isArray(colWidths) ? colWidths[i] : null;
    return w ? { width: w } : undefined;
  };

  // 0.36.689 — Auto-enable table-layout: fixed whenever any
  // col_widths entry is set. Without this, the browser defaults
  // to table-layout: auto — the <col> widths are treated as HINTS,
  // not hard limits, and columns silently expand to fit content.
  // On canvas (3 short sample rows) that expansion is barely
  // visible; on public / Preview with real data it makes the
  // first data column vividly wider and the second slightly
  // wider than admin dragged them. Explicit `fixedLayout={true}`
  // still works — it's OR-ed with the derivation for
  // backward compat.
  // 0.36.691 — Also fixed layout during an active drag so the
  // <col> preview width is honored strictly. Without this, the
  // browser might partially ignore the mid-drag width and the
  // resize feels unresponsive.
  // 0.46.22 - Sized columns use the automatic layout: min-width and
  // max-width on cells only work there. A drag on the canvas still
  // switches to fixed for its preview.
  const sized = hasColumnSizes(colSizes, lastColRest);
  const effectiveFixedLayout = fixedLayout || (dragState && !sized) || (!sized
    && Array.isArray(colWidths) && colWidths.some(w => w && Number(w) > 0)
  );
  // 0.46.23 - A drag on a sized table previews on the dragged column's
  // own size variables, so the other columns keep their sizes (a
  // switch to the fixed layout would drop them while dragging).
  const sizeStyle = (ci) => {
    if (!sized) return null;
    if (dragState && dragState.columnIndex === ci) {
      const px = `${dragState.previewWidth}px`;
      return { '--pt-min-l': px, '--pt-max-l': px, '--pt-min-s': px, '--pt-max-s': px };
    }
    // 0.48.69 - "The last column gets the rest" means the last DATA
    // column: an Edit link column after it stays as narrow as its link.
    return columnSizeStyle(colSizes, ci, ci === headers.length - (fitLastColumn ? 2 : 1), lastColRest);
  };
  const sizedCellClass = sized ? ' ' + styles.sizedCell : '';
  const _rest = restScreens(lastColRest);
  const _restIdx = headers.length - (fitLastColumn ? 2 : 1);
  const restCellClass = (ci) => (sized && ci === _restIdx
    ? (_rest.l ? ' ' + styles.restCellL : '') + (_rest.s ? ' ' + styles.restCellS : '') : '');
  const _H = new Set(['left', 'center', 'right']);
  const _V = new Set(['top', 'middle', 'bottom']);
  const alignOf = (ci) => (fitLastColumn && ci === headers.length - 1
    ? editAlign : (Array.isArray(colAlign) ? colAlign[ci] : null));
  const alignStyle = (a, withV) => {
    if (!a || typeof a !== 'object') return null;
    const out = {};
    if (_H.has(a.h)) out.textAlign = a.h;
    if (withV && _V.has(a.v)) out.verticalAlign = a.v;
    return Object.keys(out).length ? out : null;
  };
  // 0.46.25 - Any number of columns (was two only).
  const canStack = !showHeader && headers.length > 1;

  return (
    <div className={`${styles.tableScrollWrapper} ${canStack && stackLarge ? styles.stackLarge : ''} ${canStack && stackSmall ? styles.stackSmall : ''} ${horizontalBordersOnly ? styles.horizontalOnly : ''} ${hideEdgeLines ? styles.noEdgeLines : ''} ${hideTopBottomLines ? styles.noTopBottomLines : ''} ${hideSideLines ? styles.noSideLines : ''} ${hideVerticalLines ? styles.noVerticalLines : ''} ${hideHorizontalLines ? styles.noHorizontalLines : ''}${sized && !_rest.l ? ' ' + styles.fitWrapL : ''}${sized && !_rest.s ? ' ' + styles.fitWrapS : ''}`}
         style={wrapperStyle}>
      <table
        className={styles.table
          + (sized ? ' ' + styles.sizedTable + (_rest.l ? ' ' + styles.sizedTableRestL : '') + (_rest.s ? ' ' + styles.sizedTableRestS : '') : '')}
        // 0.36.397 — When the caller asks for a fixed layout
        // (typically because they've supplied explicit widths for
        // every column), pin the browser to those widths so cell
        // content doesn't push the columns around. Used by
        // AdminLibraryDetail so switching language tabs doesn't
        // reflow the entries table.
        //
        // 0.36.411 briefly used `width: max-content` here; 0.36.412
        // reverted to the default `width: 100%` — the right pattern
        // for narrow tables is to let the text column absorb slack
        // (null width on the text column in colWidths), not to
        // shrink the table itself. See AdminLibraryDetail's
        // colWidths memo for the current strategy.
        //
        // 0.37.01 — growToContent adds `min-width: max-content` so
        // the table expands to fit long unbreakable tokens (like
        // "present_application") instead of clipping them. The
        // wrapper's overflow-x: auto then surfaces the horizontal
        // scrollbar. Cells still wrap at whitespace normally. Only
        // applies when opted in; existing tables with explicit
        // colWidths are unaffected.
        style={{
          ...(effectiveFixedLayout ? { tableLayout: 'fixed' } : {}),
          ...(growToContent ? { minWidth: 'max-content' } : {}),
        }}
      >
        {((!sized && colWidths && colWidths.length > 0) || checkable || dragState || (resizableColumns && resizableColumns.some(Boolean))) ? (
          <colgroup>
            {/* 0.36.673 — When `checkable` is on, PageTable prepends
                an extra data column for the row-select checkbox
                (see thead / tbody below). The colgroup here has to
                match that column layout — otherwise widths would
                bind to the wrong columns. Prepend a 44px <col> for
                the checkbox position, then admin-provided widths
                for the data columns.
                0.36.691 — colgroup now always renders when resize
                is possible (colWidths OR drag preview OR resizable
                columns). Otherwise the first drag would have no
                <col> to apply preview width to. Individual <col>s
                use colStyleFor(i) so drag preview supersedes stored
                width during motion. */}
            {checkable && <col style={{ width: 44 }} />}
            {headers.map((_h, i) => (
              <col key={i} style={sized ? undefined : colStyleFor(i)} />
            ))}
          </colgroup>
        ) : null}
        {showHeader && (
        <thead>
          <tr className={styles.headerRow}>
            {/* 0.36.667 — Checkable-mode header cell. Unlabelled;
                narrow (44px is enough for a 24px checkbox + 10px
                padding either side). Rendered before all data
                columns.
                0.36.682 — Now hosts a tri-state master checkbox
                that flips the selection state for the current page
                ('page' mode) or activates the "select every row"
                sentinel ('all' mode). See headerCheckboxState /
                onHeaderCheckboxClick above. */}
            {checkable && (
              <th className={`${styles.headerCell} ${styles.checkableCell}`}
                  style={alignStyle(checkAlign, false) || undefined}
                  aria-label="Row selection">
                <input
                  type="checkbox"
                  className={`${styles.rowCheckbox} ${
                    headerCheckboxState === 'indeterminate' ? styles.rowCheckboxIndeterminate : ''
                  }`}
                  checked={headerCheckboxState === 'checked'}
                  ref={(el) => {
                    if (el) el.indeterminate = (headerCheckboxState === 'indeterminate');
                  }}
                  onChange={onHeaderCheckboxClick}
                  aria-label="Select all rows"
                  aria-checked={
                    headerCheckboxState === 'indeterminate'
                      ? 'mixed'
                      : headerCheckboxState === 'checked'
                      ? 'true' : 'false'
                  }
                />
              </th>
            )}
            {headers.map((h, i) => {
              const isActive = sortState.columnIndex === i;
              const dir = isActive ? sortState.direction : null;
              const columnSortable = isSortableColumn(i);
              // 0.36.685 — Canvas wrap trumps the per-column
              // wrapHeaderColumns opt-in (used by AdminLibraryDetail).
              // 0.46.22 - A sized column's header wraps, or it would
              // overflow its largest width.
              const shouldWrap = wrapAllHeaders || sized
                || (wrapHeaderColumns && wrapHeaderColumns[i]);
              const headerTextClass = shouldWrap
                ? `${styles.headerText} ${styles.headerTextWrap}`
                : styles.headerText;
              return (
                <th key={i}
                    className={styles.headerCell
                      /* 0.38.11 - Match body-cell behavior: leading
                       * action-column headers also get the rigid-width
                       * .actionCell class so header and body cells
                       * clamp to the same 40px, preventing a browser
                       * layout edge case where the wider header padding
                       * (12px/16px vs .actionCell's 4px) could report
                       * a larger intrinsic width and skew the fixed
                       * layout when a neighboring column was resized.
                       */
                      + (i < actionColumnCount ? ' ' + styles.actionCell : '')
                      + (trailingActionColumnCount > 0
                         && i >= headers.length - trailingActionColumnCount
                         /* 0.40.26 - HEADER variant: borderless like the
                            body cells, but it KEEPS the header fill.
                            With actionCellOutside here the sticky
                            header was transparent over these columns,
                            so body rows (and their icons) showed
                            through it while scrolling. */
                         ? ' ' + styles.actionCell + ' ' + styles.actionCellOutsideHeader
                           /* 0.41.54 - Without a sticky header there is
                              no shadow to carry the underline, so the
                              border is put back explicitly. */
                           + (stickyHeader ? '' : ' ' + styles.actionCellHeaderBorder)
                         : '')
                      + sizedCellClass + restCellClass(i)
                      + (fitLastColumn && i === headers.length - 1 ? ' ' + styles.fitCell : '')}
                    style={(stickyHeaderStyle(i) || sizeStyle(i) || alignStyle(alignOf(i), false))
                      ? { ...(stickyHeaderStyle(i) || {}), ...(sizeStyle(i) || {}), ...(alignStyle(alignOf(i), false) || {}) } : undefined}
                    aria-sort={dir === 'asc' ? 'ascending'
                              : dir === 'desc' ? 'descending'
                              : 'none'}>
                  {columnSortable ? (
                    <button type="button"
                            className={styles.headerSortBtn}
                            onClick={() => handleHeaderClick(i)}
                            aria-label={`Sort by ${h}`}>
                      {/* 0.37.06 — Sort icon moved BEFORE the header
                          text (was after). .headerSortBtn is a flex
                          row so DOM order = visual order; header
                          text stays left-aligned via its own text
                          alignment. */}
                      <span className={styles.sortIcon}>
                        {dir === 'asc' ? <SortAscIcon />
                         : dir === 'desc' ? <SortDescIcon />
                         : <SortNeutralIcon />}
                      </span>
                      {renderHeaderContent(h, headerTextClass)}
                    </button>
                  ) : (
                    renderHeaderContent(h, headerTextClass)
                  )}
                  {/* 0.36.588 — Column-resize handle. Absolute, right
                      edge. Native pointer events; no external lib.
                      0.36.691 — Drag now updates React state (dragState)
                      instead of the th's inline style. Under
                      table-layout: fixed (auto-enabled since ship
                      0.36.689 when col_widths is set), inline th
                      widths are ignored — the <col> in <colgroup>
                      dominates. So the drag preview has to update
                      the <col>'s effective width via state → colgroup
                      re-renders each mousemove. Also adds a vertical
                      dotted guide line that follows the cursor so
                      admin sees exactly where the border will land. */}
                  {resizableColumns && resizableColumns[i] && onColumnResize && (
                    <span
                      className={styles.resizeHandle + (resizeHandlesVisible ? ' ' + styles.resizeHandleVisible : '')}
                      aria-hidden="true"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const th = e.currentTarget.parentElement;
                        const startX = e.clientX;
                        const startWidth = th ? th.getBoundingClientRect().width : 100;
                        const DRAG_THRESHOLD = 2;
                        let moved = false;
                        const clamp = (w) => Math.max(20, Math.min(4000, w));
                        const onMove = (mv) => {
                          const dx = mv.clientX - startX;
                          if (Math.abs(dx) > DRAG_THRESHOLD) moved = true;
                          const w = clamp(startWidth + dx);
                          setDragState({
                            columnIndex: i,
                            previewWidth: w,
                            guideX: mv.clientX,
                          });
                        };
                        const onUp = (mv) => {
                          window.removeEventListener('mousemove', onMove);
                          window.removeEventListener('mouseup', onUp);
                          setDragState(null);
                          if (!moved) return;
                          const dx = mv.clientX - startX;
                          const w = clamp(Math.round(startWidth + dx));
                          onColumnResize(i, w);
                        };
                        window.addEventListener('mousemove', onMove);
                        window.addEventListener('mouseup', onUp);
                      }}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        )}
        <tbody>
          {displayedRows.map((row, ri) => {
            // 0.36.407 — Row identifier for scroll-into-view targeting.
            // 0.36.409 — Uses sortedRowKeys so the identifier follows
            // the row through client-side sort. Was reading from the
            // original rowKeys with a paginationState-only offset,
            // which broke correspondence once headers became clickable.
            // 0.36.700 — Skip the pagination offset when `preSliced`
            // is true. In server-paginated mode (large libraries),
            // sortedRowKeys already covers only the current page's
            // rows — adding an offset made the lookup miss on every
            // page after 1, producing undefined rowKeys and forcing
            // the fallback to a positional index; header ops (which
            // correctly use displayedRowKeys) then diverged from
            // per-row ops.
            const originalIdx = paginationState && !preSliced
              ? (paginationState.currentPage - 1) * paginationState.perPage + ri
              : ri;
            const rowKey = sortedRowKeys && sortedRowKeys[originalIdx] != null
              ? String(sortedRowKeys[originalIdx])
              : undefined;
            const extraRowClass = rowClassNames && rowClassNames[originalIdx]
              ? ` ${rowClassNames[originalIdx]}`
              : '';
            return (
              <tr
                key={ri}
                className={styles.bodyRow + extraRowClass}
                style={rowColorStyle(tableColors, ri)}
                data-row-key={rowKey}
              >
                {/* 0.36.667 — Checkable-mode body cell. Small (24×24)
                    Henaket checkbox. Row identifier prefers `rowKey`
                    (stable across sort / pagination) when available,
                    falls back to `originalIdx`.
                    0.36.682 — Now derives its checked state via
                    isRowChecked (mode-aware) instead of a flat Set
                    membership. */}
                {checkable && (() => {
                  // 0.36.705 — Read the definitive rowId from the
                  // memo (displayedRowIds). Header, click handler,
                  // and this cell all share the same source of
                  // truth — duplicate/empty keys already folded to
                  // positional in one place.
                  const rowId = displayedRowIds[ri];
                  const isChecked = isRowChecked(rowId);
                  return (
                    <td className={`${styles.bodyCell} ${styles.checkableCell}`}
                        style={alignStyle(checkAlign, true)
                          /* At the top or bottom the box keeps the cells' 8px, so it
                             lines up with the first or last line of text. */
                          ? { ...alignStyle(checkAlign, true),
                              ...(checkAlign.v === 'top' || checkAlign.v === 'bottom' ? { paddingTop: 8, paddingBottom: 8 } : {}) }
                          : undefined}>
                      <input
                        type="checkbox"
                        className={styles.rowCheckbox}
                        checked={isChecked}
                        onChange={() => toggleRowChecked(rowId)}
                        aria-label={`Select row ${originalIdx + 1}`}
                      />
                    </td>
                  );
                })()}
                {headers.map((_, ci) => (
                  <td
                    key={ci}
                    className={
                      `${styles.bodyCell}` +
                      (wrapAllCells ? ' ' + styles.bodyCellWrap : '') +
                      (ci < actionColumnCount ? ' ' + styles.actionCell : '') +
                      (trailingActionColumnCount > 0
                       && ci >= row.length - trailingActionColumnCount
                       ? ' ' + styles.actionCell + ' ' + styles.actionCellOutside
                       : '') + sizedCellClass + restCellClass(ci)
                      + (fitLastColumn && ci === headers.length - 1 ? ' ' + styles.fitCell : '')
                    }
                    style={{ ...(stickyBodyStyle(ci) || {}), ...columnTextStyle(columnTextStyles, ci), ...(sizeStyle(ci) || {}), ...(alignStyle(alignOf(ci), true) || {}) }}
                  >
                    {row[ci] ?? ''}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* 0.36.691 — Vertical dotted guide line, follows cursor
        * during column resize drag. Position: fixed so it spans
        * the viewport; pointer-events: none so it doesn't
        * interfere with the drag. Renders only while dragState
        * is active. */}
      {dragState && (
        <div
          aria-hidden="true"
          style={{
            position: 'fixed',
            top: 0,
            bottom: 0,
            left: dragState.guideX,
            width: 0,
            borderLeft: '1px dashed var(--color-blue-800)',
            pointerEvents: 'none',
            zIndex: 9999,
          }}
        />
      )}
    </div>
  );
}
